/**
 * Admin Businesses Page - displays all businesses in a table format
 * Accessible only to super_admin users
 * @component AdminBusinessesPage
 * @file AdminBusinessesPage.tsx
 * @path frontend/src/pages/AdminBusinessesPage/AdminBusinessesPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import LoadingSpinner from '../../components/LoadingSpinner';
import Tooltip from '../../components/Tooltip';
import Popconfirm from '../../components/Popconfirm/Popconfirm';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import PackageAssignmentModal from '../../modals/PackageAssignmentModal';
import { IconButton, Button, TablePagination, Tabs, Tab, Box, Collapse, Table, TableBody, TableCell, TableHead, TableRow, Chip, TableContainer, Paper, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import LockResetIcon from '@mui/icons-material/LockReset';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CancelIcon from '@mui/icons-material/Cancel';
import { useIsMobile } from '../../hooks/useResponsive';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';
import { TranslationKey } from '../../locales/types';
import html2pdf from 'html2pdf.js';
import './AdminBusinessesPage.css';

interface NullString {
  String: string;
  Valid: boolean;
}

interface AdminUserBusiness {
  // User information
  user_id: string;
  user_email: NullString;
  user_country_code: NullString;
  user_phone: NullString;
  user_full_name: NullString;
  user_first_name: NullString;
  user_last_name: NullString;
  user_role: string;
  user_is_verified: boolean;
  user_phone_verified: boolean;
  user_created_at: string;
  user_updated_at: string;
  user_last_login_at: string | null;

  // Business information (nullable)
  business_id?: string;
  business_name?: string;
  business_description?: string;
  business_phone?: string;
  business_email: NullString;
  business_website: NullString;
  business_is_active?: boolean;
  business_created_at?: string;
  business_logo_url?: string;
  business_city?: string;
  business_street?: string;
  business_building?: string;
  business_categories?: string;
  business_services_count?: number;
  business_services_list?: string;

  // Package information
  package_name: string;
  package_active: boolean;
  package_expires_at: string | null;
}



interface AdminClient {
  id: string;
  email: NullString;
  country_code: NullString;
  phone: NullString;
  full_name: NullString;
  first_name: NullString;
  last_name: NullString;
  role: string;
  is_verified: boolean;
  phone_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  business_name: NullString;
  business_type: NullString;
  business_address: NullString;
  business_phone: NullString;
  business_website: NullString;
  business_logo: NullString;
  business_description: NullString;
  facebook_url: NullString;
  instagram_url: NullString;
  twitter_url: NullString;
  linkedin_url: NullString;
  salon_activated: boolean;
  salon_id: NullString;
  package_name: string;
  package_active: boolean;
  package_expires_at: string | null;
}

const AdminBusinessesPage: React.FC = () => {
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const t = (key: TranslationKey) => translate(key, language as 'en' | 'ua');
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState<AdminUserBusiness[]>([]);
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: string;
    ownerName: string;
  }>({
    open: false,
    userId: '',
    ownerName: '',
  });

  const [packageModal, setPackageModal] = useState<{
    open: boolean;
    userId: string;
    userName: string;
    userRole: string;
    currentPackage?: string;
    currentPackageExpiresAt?: string | null;
  }>({
    open: false,
    userId: '',
    userName: '',
    userRole: '',
    currentPackageExpiresAt: null,
  });

  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // User subscriptions state for icon colors
  const [userSubscriptions, setUserSubscriptions] = useState<Record<string, boolean>>({});

  // Subscription management state
  const [subscriptionModal, setSubscriptionModal] = useState<{
    open: boolean;
    userId: string;
    userName: string;
    subscriptions: any[];
    loading: boolean;
  }>({
    open: false,
    userId: '',
    userName: '',
    subscriptions: [],
    loading: false,
  });

  const [cancelSubscriptionDialog, setCancelSubscriptionDialog] = useState<{
    open: boolean;
    subscriptionId: string;
    userId: string;
    subscriptionDetails: any;
  }>({
    open: false,
    subscriptionId: '',
    userId: '',
    subscriptionDetails: null,
  });

  useEffect(() => {
    if (activeTab === 0) {
      fetchBusinesses();
    } else {
      fetchClients();
    }
  }, [activeTab, pagination.page, pagination.limit]);

  // Check subscriptions for all users when data is loaded
  useEffect(() => {
    if (activeTab === 0 && users.length > 0) {
      const uniqueUserIds = Array.from(new Set(users.map(user => user.user_id).filter(Boolean)));
      uniqueUserIds.forEach(userId => {
        checkUserSubscriptions(userId);
      });
    }
  }, [users, activeTab]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all users except clients with pagination
      const params = new URLSearchParams({
        page: (pagination.page + 1).toString(), // Backend expects 1-based pages
        limit: pagination.limit.toString(),
      });

      const response = await fetch(`${config.api.baseUrl}/admin/businesses?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to fetch users');
      }

      const data = await response.json();

      console.log('API Response:', {
        total: data.total,
        usersCount: data.users?.length,
        users: data.users
      });

      // Handle the response as object with users array
      const usersArray: AdminUserBusiness[] = data.users.map((user: any) => ({
        user_id: user.user_id,
        user_email: user.user_email,
        user_country_code: user.user_country_code,
        user_phone: user.user_phone,
        user_full_name: user.user_full_name,
        user_first_name: user.user_first_name,
        user_last_name: user.user_last_name,
        user_role: user.user_role,
        user_is_verified: user.user_is_verified,
        user_phone_verified: user.user_phone_verified,
        user_created_at: user.user_created_at,
        user_updated_at: user.user_updated_at,
        user_last_login_at: user.user_last_login_at,
        business_id: user.business_id,
        business_name: user.business_name,
        business_description: user.business_description,
        business_phone: user.business_phone,
        business_email: user.business_email,
        business_website: user.business_website,
        business_is_active: user.business_is_active,
        business_created_at: user.business_created_at,
        business_city: user.business_city,
        business_street: user.business_street,
        business_building: user.business_building,
        business_categories: user.business_categories,
        business_services_count: user.business_services_count,
        business_services_list: user.business_services_list,
        package_name: user.package_name,
        package_active: user.package_active,
        package_expires_at: user.package_expires_at,
      }));

      setUsers(usersArray);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
      }));
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${config.api.baseUrl}/admin/clients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to fetch clients');
      }

      const data: AdminClient[] = await response.json();
      setClients(data || []);
      setPagination(prev => ({
        ...prev,
        total: data.length || 0,
      }));
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string, isMobile: boolean = false) => {
    if (isMobile) {
      // Shorter format for mobile: DD.MM.YY with time on new line
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('uk-UA', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      });
      const timeStr = date.toLocaleTimeString('uk-UA', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return (
        <div className="mobile-date">
          <div>{dateStr}</div>
          <div>{timeStr}</div>
        </div>
      );
    }

    // Full format for desktop
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRegeneratePasswordClick = (userId: string, ownerName: string) => {
    setConfirmDialog({
      open: true,
      userId,
      ownerName,
    });
  };

  const generatePasswordPDF = (ownerName: string, login: string, password: string) => {
    // Create HTML template with proper Ukrainian text and CSS
    const htmlContent = `
      <div style="
        font-family: 'Arial', 'Helvetica', sans-serif;
        max-width: 600px;
        margin: 0 auto;
        padding: 20px;
        background: white;
        color: #333;
      ">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 20px;">
          <h1 style="color: #2c3e50; margin: 0; font-size: 18px; font-weight: bold;">
            СИСТЕМА УПРАВЛІННЯ БІЗНЕСОМ
          </h1>
        </div>

        <!-- Main Title -->
        <div style="text-align: center; margin-bottom: 10px;">
          <h2 style="color: #2c3e50; margin: 0; font-size: 16px; font-weight: bold;">
            НОВІ ДАНІ ДЛЯ ВХОДУ
          </h2>
        </div>

        <!-- Subtitle -->
        <div style="text-align: center; margin-bottom: 30px;">
          <p style="margin: 0; font-size: 12px; color: #666;">
            Інформація про обліковий запис користувача
          </p>
        </div>

        <!-- User Info -->
        <div style="margin-bottom: 20px;">
          <p style="margin: 5px 0; font-size: 11px;">
            <strong>ПІБ користувача:</strong> ${ownerName}
          </p>
          <p style="margin: 5px 0; font-size: 11px;">
            <strong>Дата генерації:</strong> ${new Date().toLocaleDateString('uk-UA')} ${new Date().toLocaleTimeString('uk-UA')}
          </p>
        </div>

        <!-- Credentials Table -->
        <table style="
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
          font-size: 11px;
        ">
          <thead>
            <tr>
              <th style="
                background-color: #3498db;
                color: white;
                padding: 8px;
                text-align: center;
                border: 1px solid #ddd;
                font-weight: bold;
              ">
                Дані облікового запису
              </th>
              <th style="
                background-color: #3498db;
                color: white;
                padding: 8px;
                text-align: center;
                border: 1px solid #ddd;
                font-weight: bold;
              ">
                Значення
              </th>
            </tr>
          </thead>
          <tbody>
            <tr style="background-color: #f9f9f9;">
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">
                Повне ім'я (ПІБ)
              </td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                ${ownerName}
              </td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">
                Логін для входу
              </td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                ${login}
              </td>
            </tr>
            <tr style="background-color: #f9f9f9;">
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">
                Новий пароль
              </td>
              <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace; font-weight: bold; color: #e74c3c;">
                ${password}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Security Notice -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #e74c3c; margin: 0 0 10px 0; font-size: 12px;">
            УВАГА!
          </h3>
          <ul style="margin: 0; padding-left: 20px; font-size: 10px; line-height: 1.4;">
            <li>Зберігайте цей документ у безпечному місці</li>
            <li>Не передавайте пароль третім особам</li>
            <li>При першому вході рекомендується змінити пароль</li>
          </ul>
        </div>

        <!-- Footer -->
        <div style="text-align: center; border-top: 1px solid #ddd; padding-top: 15px;">
          <p style="margin: 0; font-size: 8px; color: #666;">
            Документ згенеровано автоматично системою управління бізнесом
          </p>
          <p style="margin: 3px 0 0 0; font-size: 8px; color: #666;">
            Сторінка 1 з 1 • ${new Date().toLocaleString('uk-UA')}
          </p>
        </div>
      </div>
    `;

    // Configure html2pdf options
    const options = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: `дані_для_входу_${ownerName.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: {
        unit: 'mm' as const,
        format: 'a4' as const,
        orientation: 'portrait' as const
      }
    };

    // Generate and download PDF
    html2pdf().set(options).from(htmlContent).save();
  };

  const handleConfirmRegeneratePassword = async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/admin/regenerate-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
        body: JSON.stringify({ user_id: confirmDialog.userId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to regenerate password');
      }

      const data = await response.json();

      // Generate PDF with user credentials
      generatePasswordPDF(
        confirmDialog.ownerName,
        data.login, // Use login from response
        data.new_password
      );

      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: `Пароль успішно перегенеровано. PDF файл завантажено.`,
        severity: 'success',
      }]);
    } catch (err) {
      console.error('Error regenerating password:', err);
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: err instanceof Error ? err.message : 'Помилка при перегенерації пароля',
        severity: 'error',
      }]);
    } finally {
      setConfirmDialog({ open: false, userId: '', ownerName: '' });
    }
  };

  const handleCancelRegeneratePassword = () => {
    setConfirmDialog({ open: false, userId: '', ownerName: '' });
  };

  const handleCloseNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handlePackageClick = (user: AdminUserBusiness) => {
    const userName = user.user_full_name.Valid
      ? user.user_full_name.String
      : (user.user_first_name.Valid && user.user_last_name.Valid)
      ? `${user.user_last_name.String} ${user.user_first_name.String}`
      : t('adminBusinesses.unknownUser');

    setPackageModal({
      open: true,
      userId: user.user_id,
      userName,
      userRole: user.user_role,
      currentPackage: user.package_name || undefined,
      currentPackageExpiresAt: user.package_expires_at,
    });
  };

  const handlePackageModalClose = () => {
    setPackageModal({
      open: false,
      userId: '',
      userName: '',
      userRole: '',
      currentPackageExpiresAt: null,
    });
  };

  const handlePackageAssigned = () => {
    // Refresh the current tab data to show updated package information
    if (activeTab === 0) {
      fetchBusinesses();
    } else {
      fetchClients();
    }
    const notificationId = Date.now().toString();
    setNotifications(prev => [...prev, {
      id: notificationId,
      message: 'Пакет успішно призначено',
      severity: 'success',
    }]);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination(prev => ({ ...prev, limit: newLimit, page: 0 }));
  };



  const handleRunCronJob = async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/admin/cron/run-expired-packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to run cron job');
      }

      const data = await response.json();

      // Refresh the businesses list to show updated status
      await fetchBusinesses();

      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: data.message || `Cron job виконано успішно. Знайдено ${data.expired_businesses_found || 0} прострочених бізнесів.`,
        severity: 'success',
      }]);
    } catch (err) {
      console.error('Error running cron job:', err);
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: err instanceof Error ? err.message : 'Помилка запуску cron job',
        severity: 'error',
      }]);
    }
  };

  if (loading) {
    return (
      <div className="admin-businesses-page">
        <div className="loading-container">
          <LoadingSpinner size="large" />
          <p>{activeTab === 0 ? 'Завантаження бізнесів...' : 'Завантаження клієнтів...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-businesses-page">
        <div className="error-container">
          <h2>Помилка</h2>
          <p>{error}</p>
          <button onClick={() => {
            if (activeTab === 0) {
              fetchBusinesses();
            } else {
              fetchClients();
            }
          }} className="retry-button">
            Спробувати знову
          </button>
        </div>
      </div>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page when switching tabs
  };

  const handleToggleExpand = (userId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(userId)) {
      newExpandedRows.delete(userId);
    } else {
      newExpandedRows.add(userId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Subscription management functions
  const checkUserSubscriptions = async (userId: string) => {
    try {
      const token = localStorage.getItem(config.auth.tokenKey);
      const response = await fetch(`${config.api.baseUrl}/admin/users/${userId}/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const hasSubscriptions = data.subscriptions && data.subscriptions.length > 0;
        setUserSubscriptions(prev => ({ ...prev, [userId]: hasSubscriptions }));
      }
    } catch (err) {
      console.error('Failed to check user subscriptions:', err);
      setUserSubscriptions(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleViewSubscriptions = async (userId: string, userName: string) => {
    try {
      setSubscriptionModal(prev => ({ ...prev, loading: true, open: true, userId, userName, subscriptions: [] }));
      
      const token = localStorage.getItem(config.auth.tokenKey);
      const response = await fetch(`${config.api.baseUrl}/admin/users/${userId}/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch subscriptions: ${response.statusText}`);
      }

      const data = await response.json();
      const hasSubscriptions = data.subscriptions && data.subscriptions.length > 0;
      setSubscriptionModal(prev => ({ ...prev, subscriptions: data.subscriptions || [], loading: false }));
      setUserSubscriptions(prev => ({ ...prev, [userId]: hasSubscriptions }));
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setSubscriptionModal(prev => ({ ...prev, subscriptions: [], loading: false }));
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: 'Не вдалося завантажити підписки',
        severity: 'error',
      }]);
    }
  };

  const handleCancelSubscription = (subscriptionId: string, userId: string, subscriptionDetails: any) => {
    setCancelSubscriptionDialog({
      open: true,
      subscriptionId,
      userId,
      subscriptionDetails,
    });
  };

  const confirmCancelSubscription = async () => {
    try {
      const token = localStorage.getItem(config.auth.tokenKey);
      const response = await fetch(`${config.api.baseUrl}/admin/subscriptions/${cancelSubscriptionDialog.subscriptionId}/cancel`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: cancelSubscriptionDialog.userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel subscription: ${response.statusText}`);
      }

      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: 'Підписку скасовано успішно',
        severity: 'success',
      }]);
      setCancelSubscriptionDialog({ open: false, subscriptionId: '', userId: '', subscriptionDetails: null });
      
      // Update user subscriptions state
      setUserSubscriptions(prev => ({ ...prev, [cancelSubscriptionDialog.userId]: false }));
      
      // Refresh subscriptions if modal is open
      if (subscriptionModal.open) {
        handleViewSubscriptions(subscriptionModal.userId, subscriptionModal.userName);
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: 'Помилка скасування підписки',
        severity: 'error',
      }]);
    }
  };

  // Group users by user_id to handle multiple businesses per user
  const groupedUsers = users.reduce((acc, user) => {
    console.log('Processing user:', user.user_id, user.user_role, !!user.business_id);

    // Skip users with null/undefined user_id
    if (!user.user_id) {
      console.warn('Skipping user with null/undefined user_id:', user);
      return acc;
    }

    if (!acc[user.user_id]) {
      acc[user.user_id] = {
        user: user,
        businesses: []
      };
    }

    // Always update user info with the most complete user data
    if (user.user_full_name.Valid ||
        (user.user_email.Valid && !acc[user.user_id].user.user_email.Valid)) {
      acc[user.user_id].user = user;
    }

    if (user.business_id) {
      acc[user.user_id].businesses.push(user);
    }
    return acc;
  }, {} as Record<string, { user: AdminUserBusiness; businesses: AdminUserBusiness[] }>);

  console.log('Grouped users keys:', Object.keys(groupedUsers));
  console.log('Grouped users count:', Object.keys(groupedUsers).length);

  // Create a comprehensive list of all unique users (including those without businesses)
  // by merging users with businesses and users without businesses
  const allUsers = Object.values(groupedUsers).map(({ user, businesses }) => ({
    user,
    businesses
  }));

  console.log('All users count:', allUsers.length);
  console.log('All users:', allUsers.map(u => ({ id: u.user.user_id, role: u.user.user_role, businesses: u.businesses.length })));

  // Calculate statistics (after allUsers is defined)
  const totalUsers = pagination.total;
  const superAdminCount = allUsers.filter(({ user }) => user.user_role === 'super_admin').length;
  const totalUsersExcludingSuperAdmin = totalUsers - superAdminCount;
  const totalBusinesses = allUsers.reduce((sum, { businesses }) => sum + businesses.length, 0);
  const usersWithoutBusinesses = allUsers.filter(({ businesses }) => businesses.length === 0).length;

  // Show all users except super_admin
  const filteredUsers = allUsers.filter(({ user }) => user.user_role !== 'super_admin');

  console.log('Filtered users count:', filteredUsers.length);

  return (
    <div className="admin-businesses-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>{activeTab === 0 ? t('adminBusinesses.title') : t('adminBusinesses.clients')}</h1>
            {activeTab === 0 ? (
              <>
                <p>{t('adminBusinesses.totalUsers')} {totalUsersExcludingSuperAdmin}</p>
                <p>{t('adminBusinesses.totalBusinesses')} {totalBusinesses}</p>
                <p>{t('adminBusinesses.usersWithoutBusinesses')} {usersWithoutBusinesses}</p>
              </>
            ) : (
              <p>{t('adminBusinesses.totalClients')} {pagination.total}</p>
            )}
          </div>
          {activeTab === 0 && (
            <div className="header-actions">
              <Tooltip content={<>Запускає перевірку та обробку<br />прострочених пакетів користувачів.<br />Автоматично деактивує бізнеси з<br />закінченим терміном дії пакетів</>} position="top">
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleRunCronJob}
                  size="small"
                >
                  Запустити Cron Job
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="admin tabs">
          <Tab icon={<BusinessIcon />} label={t('adminBusinesses.all')} iconPosition="start" />
          <Tab icon={<PeopleIcon />} label={t('adminBusinesses.clients')} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ fontWeight: 'bold' }}></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('adminBusinesses.tableHeaders.user')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('adminBusinesses.tableHeaders.userContacts')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('adminBusinesses.tableHeaders.package')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('adminBusinesses.tableHeaders.verificationStatus')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('adminBusinesses.tableHeaders.lastLogin')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('adminBusinesses.tableHeaders.created')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('adminBusinesses.tableHeaders.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map(({ user, businesses }) => {
                const userId = user.user_id;
                const isExpanded = expandedRows.has(userId);

                return (
                  <React.Fragment key={userId}>
                    <TableRow
                      hover
                      sx={{
                        '& .MuiTableCell-root': { color: 'primary.main' },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleToggleExpand(userId)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleExpand(userId)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        <div className="user-info-cell">
                          <div className="user-full-name">
                            {user.user_full_name.Valid
                              ? user.user_full_name.String
                              : (user.user_first_name.Valid && user.user_last_name.Valid)
                              ? `${user.user_last_name.String} ${user.user_first_name.String}`
                              : 'Невідомий користувач'
                            }
                          </div>
                          <div className="user-role">Роль: {user.user_role}</div>
                          <div className="user-verification">
                            {user.user_is_verified && <span className="verified-badge">✓</span>}
                            {user.user_phone_verified && <span className="phone-verified-badge">📱</span>}
                          </div>
                          <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#666' }}>
                            Бізнесів: {businesses.length}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="contact-info">
                          {user.user_email.Valid && user.user_email.String && (
                            <div className="contact-item">
                              <EmailIcon sx={{ color: '#007bff', fontSize: 14, marginRight: 0.5 }} />
                              <span className="user-email">{user.user_email.String}</span>
                            </div>
                          )}
                          <div className="contact-item">
                            <PhoneIcon sx={{ color: '#007bff', fontSize: 14, marginRight: 0.5 }} />
                            {user.user_phone.Valid && user.user_phone.String ? (
                              <a href={`tel:${user.user_phone.String}`} className="user-phone">
                                {user.user_phone.String.replace(/^\+?38/, '')}
                              </a>
                            ) : (
                              <span className="user-missing-phone">відсутній</span>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div
                          className="package-info clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePackageClick(user);
                          }}
                          title="Натисніть для призначення пакета"
                        >
                          <div className="package-name">
                            {user.package_name === 'Free' && user.package_active ? (
                              <>
                                {user.package_name} <span style={{
                                  backgroundColor: '#d4edda',
                                  color: '#155724',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  padding: '2px 6px',
                                  borderRadius: '10px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}>активний</span>
                              </>
                            ) : (user.package_name || 'Без пакета')}
                          </div>
                          {user.package_active && user.package_name !== 'Free' && (
                            <div className="package-status active">Активний</div>
                          )}

                          {user.package_active && user.package_expires_at && (
                            <div className="package-expiry">
                              Дійсний до: {new Date(user.package_expires_at).toLocaleDateString('uk-UA', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                          {user.package_active && !user.package_expires_at && (
                            <div className="package-expiry">
                              Без терміну дії
                            </div>
                          )}
                          {!user.package_active && (
                            <div className="package-status inactive">Неактивний</div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="verification-status">
                          <span className={`status-badge ${user.user_is_verified ? 'verified' : 'unverified'}`}>
                            {user.user_is_verified ? 'Верифікований' : 'Не верифікований'}
                          </span>
                          {user.user_phone_verified && (
                            <span className="phone-verified-badge">Телефон підтверджено</span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="last-login-info">
                          {user.user_last_login_at ? formatDate(user.user_last_login_at, isMobile) : 'Ніколи'}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="date-info">
                          {formatDate(user.user_created_at, isMobile)}
                        </div>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="actions-cell">
                          <Tooltip content="Змінити пароль" position="top" maxWidth={150}>
                            <IconButton
                              onClick={() => handleRegeneratePasswordClick(
                                user.user_id,
                                user.user_full_name.Valid
                                  ? user.user_full_name.String
                                  : (user.user_first_name.Valid && user.user_last_name.Valid)
                                  ? `${user.user_last_name.String} ${user.user_first_name.String}`
                                  : 'Невідомий користувач'
                              )}
                              size="small"
                              color="primary"
                            >
                              <LockResetIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip content="Підписки користувача" position="top" maxWidth={150}>
                            <IconButton
                              onClick={() => handleViewSubscriptions(
                                user.user_id,
                                user.user_full_name.Valid
                                  ? user.user_full_name.String
                                  : (user.user_first_name.Valid && user.user_last_name.Valid)
                                  ? `${user.user_last_name.String} ${user.user_first_name.String}`
                                  : 'Невідомий користувач'
                              )}
                              size="small"
                              color={userSubscriptions[user.user_id] ? "success" : "default"}
                            >
                              <CreditCardIcon 
                                fontSize="small" 
                                sx={{ 
                                  color: userSubscriptions[user.user_id] ? '#06e90eff' : '#9e9e9e' 
                                }} 
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                              {t('adminBusinesses.userBusinesses')} ({businesses.length})
                            </Typography>

                            {businesses.length === 0 ? (
                              <Box display="flex" justifyContent="center" p={2}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('adminBusinesses.noBusinesses')}
                                </Typography>
                              </Box>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Назва бізнесу</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Контакти</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Адреса</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Категорії</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Створено</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {businesses.map((business) => (
                                    <TableRow key={business.business_id}>
                                      <TableCell>
                                        <div className="business-name">{business.business_name}</div>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={business.business_is_active ? 'Активний' : 'Неактивний'}
                                          color={business.business_is_active ? 'success' : 'default'}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <div className="contact-info">
                                          {business.business_phone && (
                                            <div className="contact-item">
                                              <PhoneIcon sx={{ color: '#007bff', fontSize: 14, marginRight: 0.5 }} />
                                              <span className="business-phone">{business.business_phone}</span>
                                            </div>
                                          )}
                                          {business.business_email.Valid && business.business_email.String && (
                                            <div className="contact-item">
                                              <EmailIcon sx={{ color: '#007bff', fontSize: 14, marginRight: 0.5 }} />
                                              <span className="business-email">{business.business_email.String}</span>
                                            </div>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="address-info">
                                          {business.business_city && <div>{business.business_city}</div>}
                                          {business.business_street && <div>{business.business_street} {business.business_building}</div>}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        {business.business_categories ? (
                                          <div className="business-categories">
                                            {business.business_categories.split(', ').slice(0, 2).join(', ')}
                                            {business.business_categories.split(', ').length > 2 && '...'}
                                          </div>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            Без категорій
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {formatDate(business.business_created_at || business.user_created_at, isMobile)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className="clients-table-container">
          <table className="clients-table">
            <thead>
              <tr>
                <th>ПІБ</th>
                <th>Контакти</th>
                <th>Пакет</th>
                <th>Статус верифікації</th>
                <th>Останній вхід</th>
                <th>Створено</th>
                <th>Дії</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <div className="client-name-cell">
                      <div className="client-full-name">
                        {client.full_name.Valid
                          ? client.full_name.String
                          : (client.first_name.Valid && client.last_name.Valid)
                          ? `${client.last_name.String} ${client.first_name.String}`
                          : 'Невідомий клієнт'
                        }
                      </div>
                      <div className="client-role">Роль: {client.role}</div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      {client.email.Valid && client.email.String && (
                        <div className="contact-item">
                          <EmailIcon sx={{ color: '#007bff', fontSize: 14, marginRight: 0.5 }} />
                          <span className="client-email">{client.email.String}</span>
                        </div>
                      )}
                      <div className="contact-item">
                        <PhoneIcon sx={{ color: '#007bff', fontSize: 14, marginRight: 0.5 }} />
                        {client.phone.Valid && client.phone.String ? (
                          <a href={`tel:${client.phone.String}`} className="client-phone">
                            {client.phone.String.replace(/^\+?38/, '')}
                          </a>
                        ) : (
                          <span className="client-missing-phone">відсутній</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div
                      className="package-info clickable"
                      onClick={() => {
                        const userName = client.full_name.Valid ? client.full_name.String : 'Невідомий клієнт';
                        setPackageModal({
                          open: true,
                          userId: client.id,
                          userName,
                          userRole: client.role,
                          currentPackage: client.package_name || undefined,
                          currentPackageExpiresAt: client.package_expires_at,
                        });
                      }}
                      title="Натисніть для призначення пакета"
                    >
                      <div className="package-name">
                        {client.package_name === 'Free' && client.package_active ? (
                          <>
                            {client.package_name} <span style={{
                              backgroundColor: '#d4edda',
                              color: '#155724',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              padding: '2px 6px',
                              borderRadius: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px'
                            }}>активний</span>
                          </>
                        ) : (client.package_name || 'Без пакета')}
                      </div>
                      {client.package_active && client.package_name !== 'Free' && (
                        <div className="package-status active">Активний</div>
                      )}

                      {client.package_active && client.package_expires_at && (
                        <div className="package-expiry">
                          Дійсний до: {new Date(client.package_expires_at).toLocaleDateString('uk-UA', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                      )}
                      {client.package_active && !client.package_expires_at && (
                        <div className="package-expiry">
                          Без терміну дії
                        </div>
                      )}
                      {!client.package_active && (
                        <div className="package-status inactive">Неактивний</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="verification-status">
                      <span className={`status-badge ${client.is_verified ? 'verified' : 'unverified'}`}>
                        {client.is_verified ? 'Верифікований' : 'Не верифікований'}
                      </span>
                      {client.phone_verified && (
                        <span className="phone-verified-badge">Телефон підтверджено</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="date-info">
                      {client.last_login_at ? formatDate(client.last_login_at, isMobile) : 'Ніколи'}
                    </div>
                  </td>
                  <td>
                    <div className="date-info">
                      {formatDate(client.created_at, isMobile)}
                    </div>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <Tooltip content="Змінити пароль" position="top" maxWidth={150}>
                        <IconButton
                          onClick={() => handleRegeneratePasswordClick(
                            client.id,
                            client.full_name.Valid ? client.full_name.String : 'Невідомий клієнт'
                          )}
                          size="small"
                          color="primary"
                        >
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 0 && (
        <>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Рядків на сторінці:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} з ${count !== -1 ? count : `більше ніж ${to}`}`
            }
          />

          {users.length === 0 && (
            <div className="no-users">
              <p>{t('adminBusinesses.noUsers')}</p>
            </div>
          )}
        </>
      )}

      {activeTab === 1 && clients.length === 0 && (
        <div className="no-clients">
          <p>Клієнти не знайдено</p>
        </div>
      )}

      {notifications.length > 0 && (
        <NotificationSnackbar
          notifications={notifications}
          onClose={handleCloseNotification}
        />
      )}

      <Popconfirm
        open={confirmDialog.open}
        onClose={handleCancelRegeneratePassword}
        onConfirm={handleConfirmRegeneratePassword}
        title={`Перегенерація пароля`}
        description={`Ви впевнені, що хочете перегенерувати пароль для користувача ${confirmDialog.ownerName}?`}
        okText="Перегенерувати"
        cancelText="Скасувати"
        okButtonVariant="danger"
      />

      <PackageAssignmentModal
        isOpen={packageModal.open}
        onClose={handlePackageModalClose}
        onSuccess={handlePackageAssigned}
        userId={packageModal.userId}
        userName={packageModal.userName}
        currentPackage={packageModal.currentPackage}
        currentPackageExpiresAt={packageModal.currentPackageExpiresAt}
        userRole={packageModal.userRole}
      />

      {/* Subscription Modal */}
      {subscriptionModal.open && (
        <div className="modal-overlay" onClick={() => setSubscriptionModal(prev => ({ ...prev, open: false }))}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Підписки користувача: {subscriptionModal.userName}</h3>
              <button 
                className="modal-close"
                onClick={() => setSubscriptionModal(prev => ({ ...prev, open: false }))}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {subscriptionModal.loading ? (
                <div className="loading-container">
                  <LoadingSpinner size="small" />
                  <p>Завантаження підписок...</p>
                </div>
              ) : subscriptionModal.subscriptions && subscriptionModal.subscriptions.length === 0 ? (
                <div className="no-subscriptions">
                  <p>У користувача немає активних підписок</p>
                </div>
              ) : (
                <div className="subscriptions-list">
                  {subscriptionModal.subscriptions && subscriptionModal.subscriptions.map((subscription: any) => (
                    <div key={subscription.id} className="subscription-item">
                      <div className="subscription-info">
                        <h4>{subscription.package_name || 'Пакет'}</h4>
                        <p>Сума: {subscription.amount} {subscription.currency}</p>
                        <p>Статус: <span className={`status ${subscription.status}`}>{subscription.status}</span></p>
                        <p>Автоподовлення: {subscription.auto_renewal ? 'Так' : 'Ні'}</p>
                        {subscription.next_charge_date && (
                          <p>Наступний платіж: {new Date(subscription.next_charge_date).toLocaleDateString()}</p>
                        )}
                      </div>
                      {subscription.status === 'active' && (
                        <div className="subscription-actions">
                          <button
                            className="cancel-subscription-btn"
                            onClick={() => handleCancelSubscription(subscription.id, subscriptionModal.userId, subscription)}
                          >
                            <CancelIcon fontSize="small" />
                            Скасувати
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Dialog */}
      {cancelSubscriptionDialog.open && (
        <div className="modal-overlay" onClick={() => setCancelSubscriptionDialog({ open: false, subscriptionId: '', userId: '', subscriptionDetails: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Скасувати підписку</h3>
              <button 
                className="modal-close"
                onClick={() => setCancelSubscriptionDialog({ open: false, subscriptionId: '', userId: '', subscriptionDetails: null })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Ви впевнені, що хочете скасувати цю підписку?</p>
              {cancelSubscriptionDialog.subscriptionDetails && (
                <div className="subscription-details">
                  <p><strong>Пакет:</strong> {cancelSubscriptionDialog.subscriptionDetails.package_name}</p>
                  <p><strong>Сума:</strong> {cancelSubscriptionDialog.subscriptionDetails.amount} {cancelSubscriptionDialog.subscriptionDetails.currency}</p>
                  <p><strong>Наступний платіж:</strong> {cancelSubscriptionDialog.subscriptionDetails.next_charge_date ? 
                    new Date(cancelSubscriptionDialog.subscriptionDetails.next_charge_date).toLocaleDateString() : 
                    'Н/Д'
                  }</p>
                </div>
              )}
              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setCancelSubscriptionDialog({ open: false, subscriptionId: '', userId: '', subscriptionDetails: null })}
                >
                  Назад
                </button>
                <button
                  className="confirm-btn danger"
                  onClick={confirmCancelSubscription}
                >
                  Скасувати підписку
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinessesPage;
